import React, { useCallback, useState } from 'react';
import { Table, Tag, Space, Button, Tooltip, Image, Typography, Row, Col, Pagination, Modal, InputNumber, message } from 'antd';
import { EditOutlined, DollarOutlined, LinkOutlined, ShopOutlined, BoxPlotOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { updateVariant, onChangePagination } from '../actions';
import '../ShopifyVariants.css';

const { Text } = Typography;

const ShopifyVariantsList = ({ variants, isLoading, pagination }) => {
  const dispatch = useDispatch();
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [priceValue, setPriceValue] = useState(null);
  const [inventoryValue, setInventoryValue] = useState(null);

  const handleTableChange = (pagination) => {
    dispatch(onChangePagination(pagination.current, pagination.pageSize));
  };

  const handleEditPrice = (variant) => {
    setSelectedVariant(variant);
    setPriceValue(parseFloat(variant.price));
    setInventoryValue(variant.inventoryQuantity);
    setEditModalVisible(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedVariant) return;

    try {
      const updateData = {
        price: priceValue,
        inventoryQuantity: inventoryValue
      };

      await dispatch(updateVariant(selectedVariant.id, updateData));
      message.success('Updated variant successfully');
      setEditModalVisible(false);
      setSelectedVariant(null);
    } catch (error) {
      message.error('Failed to update variant');
    }
  };

  const getInventoryStatusClass = (quantity) => {
    if (quantity > 10) return 'inventory-status-in-stock';
    if (quantity > 0) return 'inventory-status-low-stock';
    return 'inventory-status-out-of-stock';
  };

  const getVariantImage = (variant) => {
    // Check if variant has imageId
    if (variant.imageId && variant.product?.images) {
      const image = variant.product.images.find(img => img.id.toString() === variant.imageId);
      if (image) return image.src || image.url;
    }
    
    // Fallback to first product image
    if (variant.product?.images && variant.product.images.length > 0) {
      return variant.product.images[0].src || variant.product.images[0].url;
    }
    
    return null;
  };

  const getRowClassName = useCallback((record, index) => {
    return index % 2 === 0 ? 'table-row-even' : 'table-row-odd';
  }, []);

  const columns = [
    {
      title: '#',
      key: 'index',
      width: 60,
      render: (_, __, index) => (
        <span style={{ fontSize: 12 }}>
          {(pagination.current - 1) * pagination.pageSize + index + 1}
        </span>
      )
    },
    {
      title: 'Image',
      key: 'image',
      width: 80,
      align: 'center',
      render: (_, record) => {
        const imageUrl = getVariantImage(record);
        return imageUrl ? (
          <Image
            src={imageUrl}
            className="shopify-variant-image"
            alt="Variant"
            width={40}
            height={40}
          />
        ) : (
          <div style={{ width: 40, height: 40, backgroundColor: '#f0f0f0', display: 'flex', justifyContent: 'center', alignItems: 'center', borderRadius: 4 }}>
            <BoxPlotOutlined style={{ color: '#bfbfbf' }} />
          </div>
        );
      }
    },
    {
      title: 'Variant ID',
      dataIndex: 'shopifyVariantId',
      key: 'shopifyVariantId',
      width: 120,
      render: (text, record) => (
        <a
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#1890ff', fontSize: 12 }}
          href={`https://${record.product?.store?.shopifyDomain || 'admin.shopify.com'}/admin/products/${record.product?.shopifyProductId}/variants/${text}`}
        >
          {text}
        </a>
      )
    },
    {
      title: 'Product',
      key: 'product',
      ellipsis: true,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.product?.title}</div>
          <Text type="secondary" style={{ fontSize: 11 }}>
            <ShopOutlined /> {record.product?.store?.storeName}
          </Text>
        </div>
      )
    },
    {
      title: 'Variant',
      dataIndex: 'title',
      key: 'title',
      width: 150,
      render: (text, record) => (
        <div>
          <div>{text}</div>
          {(record.option1 || record.option2 || record.option3) && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              {[record.option1, record.option2, record.option3].filter(Boolean).join(' / ')}
            </Text>
          )}
        </div>
      )
    },
    {
      title: 'SKU',
      dataIndex: 'sku',
      key: 'sku',
      width: 120,
      render: (text) => text || <Text type="secondary">-</Text>
    },
    {
      title: 'Business SKU',
      key: 'businessSku',
      width: 150,
      render: (_, record) => {
        if (record.businessSku) {
          return (
            <Tooltip title={record.businessSku.productName}>
              <Tag color="blue" className="business-sku-tag">
                <LinkOutlined /> {record.businessSku.sku}
              </Tag>
            </Tooltip>
          );
        }
        return <Tag color="default">Not Mapped</Tag>;
      }
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      width: 100,
      align: 'right',
      render: (text) => <Text strong>${parseFloat(text).toFixed(2)}</Text>
    },
    {
      title: 'Inventory',
      dataIndex: 'inventoryQuantity',
      key: 'inventoryQuantity',
      width: 100,
      align: 'center',
      render: (quantity) => (
        <span className={getInventoryStatusClass(quantity)}>
          {quantity}
        </span>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit price & inventory">
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEditPrice(record)}
            />
          </Tooltip>
        </Space>
      )
    }
  ];

  return (
    <>
      <Row justify="space-between" align="middle" className="shopify-variants-list-header">
        <Col>
          <Space direction="horizontal" size={4}>
            <Text strong style={{ fontSize: 14 }}>
              Total variants:
            </Text>
            <Pagination
              size="small"
              current={pagination?.current || 1}
              total={pagination?.total || 0}
              pageSize={pagination?.pageSize || 50}
              onChange={(page, pageSize) => dispatch(onChangePagination(page, pageSize))}
              showSizeChanger={false}
              showQuickJumper={false}
              showTotal={(total, range) => `${range[0]}-${range[1]} of ${total}`}
            />
          </Space>
        </Col>
      </Row>

      <div className="shopify-variants-list">
        <Table
          columns={columns}
          dataSource={variants.map(item => ({ ...item, key: item.id }))}
          loading={isLoading}
          rowClassName={getRowClassName}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            size: 'small',
            showQuickJumper: true,
            pageSizeOptions: ['20', '50', '100', '200'],
            showTotal: (total) => `Total ${total} variants`
          }}
          onChange={handleTableChange}
          scroll={{ x: 1400 }}
        />
      </div>

      {/* Edit Modal */}
      <Modal
        title={`Edit Variant: ${selectedVariant?.title}`}
        open={editModalVisible}
        onOk={handleSaveEdit}
        onCancel={() => {
          setEditModalVisible(false);
          setSelectedVariant(null);
        }}
        okText="Save"
        cancelText="Cancel"
      >
        {selectedVariant && (
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <div>
              <Text strong>Product: </Text>
              <Text>{selectedVariant.product?.title}</Text>
            </div>
            <div>
              <Text strong>Variant SKU: </Text>
              <Text>{selectedVariant.sku || 'N/A'}</Text>
            </div>
            <div>
              <Text strong>Price ($):</Text>
              <InputNumber
                style={{ width: '100%', marginTop: 8 }}
                min={0}
                step={0.01}
                precision={2}
                value={priceValue}
                onChange={setPriceValue}
                prefix="$"
              />
            </div>
            <div>
              <Text strong>Inventory Quantity:</Text>
              <InputNumber
                style={{ width: '100%', marginTop: 8 }}
                min={0}
                value={inventoryValue}
                onChange={setInventoryValue}
              />
            </div>
          </Space>
        )}
      </Modal>
    </>
  );
};

export default ShopifyVariantsList;
